import { Download, Delete } from "@mui/icons-material";
import { Box, Chip, Tooltip, IconButton } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { formatFileSize, StratifyDocument } from "@stratify/shared";
import { format } from "date-fns";
import { Timestamp } from "firebase-admin/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { storage } from "../../../config/firebase";
import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { deleteDocumentStart } from "../store/documentsSlice";

const getProcessingStatusColor = (status: string) => {
  switch (status) {
    case "completed":
      return "success";
    case "processing":
      return "warning";
    case "failed":
      return "error";
    default:
      return "default";
  }
};

export const usePropertyDocumentListColumns =
  (): GridColDef<StratifyDocument>[] => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleDelete = useCallback(
      (documentId: string, filename: string) => {
        if (window.confirm("Are you sure you want to delete this document?")) {
          dispatch(deleteDocumentStart({ documentId, filename }));
        }
      },
      [dispatch]
    );

    return useMemo(() => {
      const filenameColumn: GridColDef<StratifyDocument> = {
        field: "filename",
        headerName: "Name",
        flex: 1,
        minWidth: 100,
        renderCell: (params: GridRenderCellParams<StratifyDocument>) => (
          <Box
            onClick={() => navigate(`/documents/${params.row.id}`)}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              width: "100%",
              color: "primary.main",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            data-testid={`document-name-${params.value}`}
          >
            {params.value}
          </Box>
        ),
      };

      const typeColumn: GridColDef<StratifyDocument> = {
        field: "type",
        headerName: "Type",
        width: 130,
      };

      const sizeColumn: GridColDef<StratifyDocument> = {
        field: "size",
        headerName: "Size",
        width: 120,
        renderCell: (params: GridRenderCellParams<StratifyDocument>) => {
          return formatFileSize(params.row.size);
        },
      };

      const categoryColumn: GridColDef<StratifyDocument> = {
        field: "metadata.category",
        headerName: "Category",
        width: 250,
        renderCell: (params: GridRenderCellParams<StratifyDocument>) => {
          const category = params.row.metadata.category;
          if (!category) return null;
          return <Chip label={category.name} size="small" />;
        },
      };

      const statusColumn: GridColDef<StratifyDocument> = {
        field: "metadata.processing.status",
        headerName: "Status",
        width: 130,
        renderCell: (params: GridRenderCellParams<StratifyDocument>) => {
          const status = params.row.metadata.processing.status;
          return (
            <Chip
              className="status-chip"
              data-testid={`document-status-${params.row.filename}`}
              label={status}
              size="small"
              color={getProcessingStatusColor(status)}
              variant="outlined"
            />
          );
        },
      };

      const createdAtColumn: GridColDef<StratifyDocument> = {
        field: "createdAt",
        headerName: "Created",
        width: 180,
        valueFormatter: (timestamp: Timestamp) => {
          return format(timestamp.toDate(), "MMM dd, yyyy HH:mm");
        },
      };

      const actionsColumn: GridColDef<StratifyDocument> = {
        field: "actions",
        headerName: "Actions",
        width: 150,
        sortable: false,
        renderCell: (params: GridRenderCellParams<StratifyDocument>) => (
          <Box>
            <Tooltip title="Download">
              <IconButton
                size="small"
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    const storageRef = ref(storage, params.row.storageLocation);
                    const url = await getDownloadURL(storageRef);
                    window.open(url);
                  } catch (error) {
                    console.error("Error downloading file:", error);
                  }
                }}
              >
                <Download />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(params.row.id, params.row.filename);
                }}
              >
                <Delete />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      };

      // const addressColumn: GridColDef<StratifyDocument> = {
      //   field: "metadata.address",
      //   headerName: "Address",
      //   flex: 1,
      //   minWidth: 200,
      //   renderCell: (params: GridRenderCellParams<StratifyDocument>) => {
      //     const address = params.row.metadata.address;
      //     return address || "-";
      //   },
      // };

      // const strataPlanNumberColumn: GridColDef<StratifyDocument> = {
      //   field: "metadata.strataPlanNumber",
      //   headerName: "Strata Plan",
      //   // width: 150,
      //   renderCell: (params: GridRenderCellParams<StratifyDocument>) => {
      //     const strataPlanNumber = params.row.metadata.strataPlanNumber;
      //     return strataPlanNumber || "-";
      //   },
      // };

      const columns: GridColDef<StratifyDocument>[] = [
        filenameColumn,
        // addressColumn,
        // strataPlanNumberColumn,
        categoryColumn,
        statusColumn,
        createdAtColumn,
        sizeColumn,
        typeColumn,
        actionsColumn,
      ];

      return columns;
    }, []);
  };
